"use client";
import React, {
  useEffect,
  useRef,
  ReactNode,
  createContext,
  useContext,
  useState,
} from "react";
import Lenis, { LenisOptions } from "lenis";

// Optional context if later you need access to Lenis instance (e.g., programmatic scroll)
const LenisContext = createContext<Lenis | null>(null);
export const useLenisInstance = () => useContext(LenisContext);
export const useLenis = useLenisInstance;

interface LenisProviderProps {
  children: ReactNode;
  options?: Partial<LenisOptions>;
}

/**
 * LenisProvider sets up a single Lenis instance for smooth scrolling across the app.
 * It also enables smooth anchor navigation for in-page links (href starting with '#').
 */
export function LenisProvider({ children, options }: LenisProviderProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const [lenisInstance, setLenisInstance] = useState<Lenis | null>(null);

  useEffect(() => {
    // Guard against multiple initializations (Fast Refresh / hot reload)
    if (lenisRef.current) return;

    const lenis = new Lenis({
      // Sensible defaults; adjust as desired
      lerp: 0.1, // inertia (0 -> instant, 1 -> no movement)
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
      ...options,
    });
    lenisRef.current = lenis;
    setLenisInstance(lenis);

    let frame: number;
    const raf = (time: number) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    // Optional: enable smooth anchor navigation
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const link = target.closest('a[href^="#"]') as HTMLAnchorElement | null;
      if (!link) return;
      const hash = link.getAttribute("href");
      if (!hash || hash === "#") return;
      const el = document.querySelector(hash);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el as HTMLElement, { offset: 0 });
      }
    };
    document.addEventListener("click", handleAnchorClick);

    // Accessibility improvement: respect user reduced motion
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) {
      lenis.stop();
    }
    const handleReduce = () => {
      if (media.matches) lenis.stop();
      else lenis.start();
    };
    media.addEventListener("change", handleReduce);

    return () => {
      cancelAnimationFrame(frame);
      media.removeEventListener("change", handleReduce);
      document.removeEventListener("click", handleAnchorClick);
      lenis.destroy();
      lenisRef.current = null;
      setLenisInstance(null);
    };
  }, [options]);

  return (
    <LenisContext.Provider value={lenisInstance}>
      {children}
    </LenisContext.Provider>
  );
}

export default LenisProvider;
